"use client";

import { cn } from "@/lib/utils";
import { motion, MotionStyle, Transition } from "motion/react";

// Re-export ShineBorder for convenience
export { ShineBorder } from "./ShineBorder";

interface BorderBeamProps {
  /**
   * The size of the border beam.
   */
  size?: number;
  /**
   * The duration of the border beam.
   */
  duration?: number;
  /**
   * The delay of the border beam.
   */
  delay?: number;
  /**
   * The color of the border beam from.
   */
  colorFrom?: string;
  /**
   * The color of the border beam to.
   */
  colorTo?: string;
  /**
   * The motion transition of the border beam.
   */
  transition?: Transition;
  /**
   * The class name of the border beam.
   */
  className?: string;
  /**
   * The style of the border beam.
   */
  style?: React.CSSProperties;
  /**
   * Whether to reverse the animation direction.
   */
  reverse?: boolean;
  /**
   * The initial offset position (0-100).
   */
  initialOffset?: number;
  /**
   * The border width of the beam.
   */
  borderWidth?: number;
}

export const BorderBeam = ({
  className,
  size = 50,
  delay = 0,
  duration = 6,
  colorFrom = "#ffaa40",
  colorTo = "#9c40ff",
  transition,
  style,
  reverse = false,
  initialOffset = 0,
  borderWidth = 1,
}: BorderBeamProps) => {
  return (
    <div
      className="pointer-events-none absolute inset-0 rounded-[inherit] border-transparent [mask-clip:padding-box,border-box] [mask-composite:intersect] [mask-image:linear-gradient(transparent,transparent),linear-gradient(#000,#000)]"
      style={
        {
          borderWidth: `${borderWidth}px`,
        } as React.CSSProperties
      }
    >
      <motion.div
        className={cn(
          "absolute aspect-square",
          "bg-gradient-to-l from-[var(--color-from)] via-[var(--color-to)] to-transparent",
          className
        )}
        style={
          {
            width: size,
            offsetPath: `rect(0 auto auto 0 round ${size}px)`,
            "--color-from": colorFrom,
            "--color-to": colorTo,
            ...style,
          } as MotionStyle
        }
        initial={{ offsetDistance: `${initialOffset}%` }}
        animate={{
          offsetDistance: reverse
            ? [`${100 - initialOffset}%`, `${-initialOffset}%`]
            : [`${initialOffset}%`, `${100 + initialOffset}%`],
        }}
        transition={{
          repeat: Infinity,
          ease: "linear",
          duration,
          delay: -delay,
          ...transition,
        }}
      />
    </div>
  );
};

// Legacy RunningBorder component for backward compatibility
interface RunningBorderProps {
  children: React.ReactNode;
  className?: string;
  duration?: number; // duration in milliseconds
  glowSize?: number; // size of the glowing element
  colors?: {
    primary: string;
    secondary?: string;
  };
  triggerOnHover?: boolean;
  alwaysVisible?: boolean;
  borderRadius?: string;
}

export const RunningBorder: React.FC<RunningBorderProps> = ({
  children,
  className = "",
  duration = 3000,
  glowSize = 80,
  colors = {
    primary: "#3b82f6",
    secondary: "#8b5cf6",
  },
  triggerOnHover = false,
  alwaysVisible = true,
  borderRadius = "1rem",
}) => {
  return (
    <motion.div
      className={`relative overflow-hidden ${className}`}
      whileHover={triggerOnHover ? "hover" : undefined}
      initial="initial"
      style={{ borderRadius }}
    >
      {/* Moving border effect using BorderBeam */}
      <motion.div
        className={`absolute inset-0 ${alwaysVisible ? "opacity-100" : "opacity-0"}`}
        variants={{
          initial: { opacity: alwaysVisible ? 1 : 0 },
          hover: { opacity: 1 },
        }}
        transition={{ opacity: { duration: 0.2 } }}
      >
        <BorderBeam
          size={glowSize}
          duration={duration / 1000} // Convert milliseconds to seconds
          colorFrom={colors.primary}
          colorTo={colors.secondary || colors.primary}
          className="opacity-80"
        />
      </motion.div>

      {/* Content */}
      <div className="relative">{children}</div>
    </motion.div>
  );
};

// Preset variants for common use cases
export const RunningBorderPresets = {
  // Brand colors (blue to purple)
  brand: {
    colors: {
      start: "#3b82f6",
      middle: "#8b5cf6",
      end: "#06b6d4",
    },
  },

  // Warm colors (orange to red)
  warm: {
    colors: {
      start: "#f97316",
      middle: "#ef4444",
      end: "#f59e0b",
    },
  },

  // Cool colors (cyan to blue)
  cool: {
    colors: {
      start: "#06b6d4",
      middle: "#3b82f6",
      end: "#1e40af",
    },
  },

  // Monochrome (grays)
  mono: {
    colors: {
      start: "#6b7280",
      middle: "#374151",
      end: "#9ca3af",
    },
  },

  // Success (greens)
  success: {
    colors: {
      start: "#10b981",
      middle: "#059669",
      end: "#34d399",
    },
  },

  // Warning (yellows/oranges)
  warning: {
    colors: {
      start: "#f59e0b",
      middle: "#d97706",
      end: "#fbbf24",
    },
  },

  // Danger (reds)
  danger: {
    colors: {
      start: "#ef4444",
      middle: "#dc2626",
      end: "#f87171",
    },
  },
};

// Convenience components for common patterns
export const RunningBorderCard: React.FC<
  Omit<RunningBorderProps, "className"> & {
    cardClassName?: string;
  }
> = ({ children, cardClassName = "", ...props }) => (
  <RunningBorder className="rounded-xl" {...props}>
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${cardClassName}`}>{children}</div>
  </RunningBorder>
);

export const RunningBorderButton: React.FC<
  Omit<RunningBorderProps, "className"> & {
    buttonClassName?: string;
  }
> = ({ children, buttonClassName = "", ...props }) => (
  <RunningBorder className="rounded-lg inline-block" {...props}>
    <button
      className={`px-4 py-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors ${buttonClassName}`}
    >
      {children}
    </button>
  </RunningBorder>
);
